import { useState } from "react";
import { toast } from "sonner";
import { Reveal } from "../components/Reveal";
import { SEO } from "../components/SEO";
import { GROUPS, IMAGES, CONTACT } from "../data/site";
import { api } from "../lib/api";
import { Clock, Users, ArrowUpRight, Loader2, Check } from "lucide-react";

export default function Groups() {
  const [selected, setSelected] = useState(GROUPS[0].id);
  const [plan, setPlan] = useState("group_session");
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", notes: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const priceLabel = plan === "group_monthly" ? "$200" : "$50";
  const planLabel = plan === "group_monthly" ? "Monthly Package (4 sessions)" : "Single Session";

  const enroll = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.email || !form.phone) {
      toast.error("Please complete all required fields.");
      return;
    }
    setLoading(true);
    try {
      const reg = await api.post("/group/register", { ...form, group_id: selected, plan });
      const checkout = await api.post("/payments/checkout", {
        lookup_key: plan,
        origin_url: window.location.origin,
        registration_id: reg.data.registration_id,
        registration_type: "group",
      });
      window.location.href = checkout.data.checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Unable to start checkout. Please try again.");
      setLoading(false);
    }
  };

  return (
    <div data-testid="groups-page" className="pt-32">
      <SEO
        title="Therapy Groups in Wesley Chapel, FL | Beyond Blessed"
        description="Faith-based therapy groups: inner child healing, anxiety, abuse recovery, codependency, grief & teen coping. $50 per session, led by licensed clinicians."
        path="/groups"
      />
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-14 grid lg:grid-cols-2 gap-12 items-center">
        <Reveal>
          <p className="uppercase tracking-[0.3em] text-xs text-teal mb-4">Therapy Groups · $50 per person</p>
          <h1 className="font-serif text-5xl md:text-7xl text-navy leading-none">Healing is better <span className="italic text-teal">together.</span></h1>
          <p className="text-charcoal/70 max-w-lg mt-6">Small, intentionally-sized circles led by a licensed therapist. Share your journey, learn new tools, and grow alongside women who understand.</p>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="bg-white p-3 shadow-sm rotate-[1.5deg]">
            <img src={IMAGES.groups} alt="Women in a support group" className="w-full h-[420px] object-cover" />
          </div>
        </Reveal>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16" data-testid="groups-list">
        <div className="grid md:grid-cols-2 gap-5">
          {GROUPS.map((g, i) => (
            <Reveal key={g.id} delay={i * 0.08}>
              <button
                type="button"
                onClick={() => { setSelected(g.id); document.getElementById("group-enroll")?.scrollIntoView({ behavior: "smooth" }); }}
                data-testid={`group-card-${g.id}`}
                className={`w-full text-left h-full p-10 border transition-all duration-500 relative ${selected === g.id ? "border-teal bg-teal/5" : "border-gold/25 bg-white hover:border-teal"}`}
              >
                <Users size={22} className="text-gold mb-5" />
                <h3 className="font-serif text-2xl md:text-3xl text-navy mb-3">{g.title}</h3>
                <p className="text-charcoal/70 mb-6">{g.desc}</p>
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-2 text-teal text-sm font-medium"><Clock size={15} /> {g.schedule}</span>
                  <span className="text-navy font-serif text-lg">$50 <span className="text-xs text-charcoal/50 font-sans">/ person</span></span>
                </div>
                {selected === g.id && <Check className="absolute top-5 right-5 text-teal" size={22} />}
              </button>
            </Reveal>
          ))}
        </div>
      </section>

      <section id="group-enroll" className="bg-seafoam/20 py-24" data-testid="group-enroll">
        <div className="max-w-3xl mx-auto px-6 lg:px-10">
          <Reveal>
            <div className="text-center mb-10">
              <p className="uppercase tracking-[0.3em] text-xs text-teal mb-3">Reserve your seat</p>
              <h2 className="font-serif text-4xl md:text-5xl text-navy">Enroll in a group</h2>
              <p className="text-charcoal/70 mt-4">You selected <strong className="text-teal">{GROUPS.find((g) => g.id === selected)?.title}</strong>. Choose how you'd like to enroll.</p>
            </div>
          </Reveal>

          <Reveal>
            <div className="grid sm:grid-cols-2 gap-4 mb-8" data-testid="group-plan-toggle">
              {[
                { key: "group_session", label: "Single Session", price: "$50", sub: "One-time drop-in" },
                { key: "group_monthly", label: "Monthly Package", price: "$200", sub: "4 sessions · save vs weekly", featured: true },
              ].map((p) => (
                <button key={p.key} type="button" onClick={() => setPlan(p.key)} data-testid={`plan-${p.key}`}
                  className={`text-left p-7 border transition-all duration-300 relative ${plan === p.key ? "bg-navy text-cream border-navy" : "bg-white border-gold/25 hover:border-teal"}`}>
                  {p.featured && <span className="absolute top-4 right-4 text-[10px] uppercase tracking-widest bg-goldbright text-navy px-2 py-1 rounded-full">Best value</span>}
                  <p className={`text-sm mb-2 ${plan === p.key ? "text-goldbright" : "text-teal"}`}>{p.label}</p>
                  <p className="font-serif text-3xl">{p.price}</p>
                  <p className={`text-xs mt-1 ${plan === p.key ? "text-cream/60" : "text-charcoal/50"}`}>{p.sub}</p>
                  {plan === p.key && <Check className="absolute bottom-5 right-5 text-goldbright" size={20} />}
                </button>
              ))}
            </div>
          </Reveal>

          <Reveal>
            <form onSubmit={enroll} className="bg-white p-8 md:p-12 border border-gold/25" data-testid="group-form">
              <div className="grid sm:grid-cols-2 gap-8 mb-8">
                <input className="intake-input" placeholder="First name *" value={form.first_name} onChange={set("first_name")} data-testid="group-first-name" />
                <input className="intake-input" placeholder="Last name *" value={form.last_name} onChange={set("last_name")} data-testid="group-last-name" />
                <input className="intake-input" placeholder="Email *" type="email" value={form.email} onChange={set("email")} data-testid="group-email" />
                <input className="intake-input" placeholder="Phone *" value={form.phone} onChange={set("phone")} data-testid="group-phone" />
              </div>
              <textarea className="intake-input resize-none mb-8" rows={3} placeholder="Anything we should know?" value={form.notes} onChange={set("notes")} data-testid="group-notes" />
              <div className="flex flex-col sm:flex-row items-center justify-between gap-6">
                <p className="text-charcoal/60 text-sm">You'll be redirected to Stripe for your <span className="text-teal font-medium">{priceLabel}</span> {planLabel.toLowerCase()} enrollment.</p>
                <button type="submit" disabled={loading} data-testid="group-enroll-button" className="bg-navy text-cream px-9 py-4 rounded-full hover:bg-teal transition-colors inline-flex items-center gap-2 disabled:opacity-60 shrink-0">
                  {loading ? <><Loader2 size={17} className="animate-spin" /> Redirecting…</> : <>Enroll &amp; Pay <ArrowUpRight size={17} /></>}
                </button>
              </div>
            </form>
          </Reveal>
          <p className="text-center text-charcoal/50 text-sm mt-6">
            Not ready to pay? <a href={`mailto:${CONTACT.email}?subject=Therapy Group Question`} className="text-teal underline">Send us a message</a> instead.
          </p>
        </div>
      </section>
    </div>
  );
}
